import { useState, type ReactNode } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@finn-auto/design-system/atoms/accordion';
import type { BryqBoardRow } from '../../services/bryqApi';
import { BryqChip } from './BryqChip';
import { useBryq } from './BryqContext';
import { COLORS, fmtDate } from './format';
import { Dot, SlaCard } from './SlaCards';
import { BRYQ_LATE_DAYS, BRYQ_OVERDUE_DAYS } from './bryqLook';

/**
 * Bryq in the Overdue tab (Oct 2026), above the role cards, two lists:
 *
 *  - **Result in · decide the next step** — a test sent as a FILTER step is done and the candidate
 *    has not changed stage since. Moving or archiving them in Lever (or from the board) clears it.
 *    A test sent WITH an interview never lands here: that interview goes ahead anyway.
 *  - **Bryq outstanding** — sent, not finished, oldest first. Amber after BRYQ_LATE_DAYS, red after
 *    BRYQ_OVERDUE_DAYS, so a candidate who ignored the link is chased before the role moves on.
 *
 * Data comes from the board's Bryq read (BryqContext), not the board build. Nothing renders while
 * it loads or when both lists are empty.
 *
 * Sits BELOW the role cards and starts COLLAPSED (Theresa, Oct 20 2026): the roles are the tab's
 * main work. The header keeps the counts — and how many are late — so a closed block still says
 * whether it needs opening. Open/closed is remembered per browser.
 */
const OPEN_KEY = 'ta-board-bryq-open';

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOpen(open: boolean) {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // private window or blocked storage: it just opens closed next time
  }
}
export function BryqSection() {
  const bryq = useBryq();
  const decide = bryq?.view?.decide ?? [];
  const outstanding = bryq?.view?.outstanding ?? [];
  const [open, setOpen] = useState(readOpen);
  if (decide.length === 0 && outstanding.length === 0) return null;

  const late = outstanding.filter((r) => (r.daysWaiting ?? 0) >= BRYQ_OVERDUE_DAYS).length;
  const setOpenAndRemember = (next: boolean) => {
    setOpen(next);
    writeOpen(next);
  };

  // The design-system accordion, styled like the role cards above it.
  return (
    <Accordion
      type="multiple"
      value={open ? ['bryq'] : []}
      onValueChange={(v: string[]) => setOpenAndRemember(v.includes('bryq'))}
    >
      <AccordionItem value="bryq" className="rounded-xl border border-gray-200 bg-white px-4">
        <AccordionTrigger heading="h3" className="py-3 hover:no-underline">
          <div className="flex w-full flex-wrap items-center gap-2 pr-2 text-left">
            <span className="text-sm font-semibold text-gray-900">🧠 Bryq assessments</span>
            {decide.length > 0 && (
              <CountChip bg={COLORS.greenBg} text={COLORS.greenText} label={`${decide.length} to decide`} />
            )}
            {outstanding.length > 0 && (
              <CountChip bg={COLORS.cotton} text={COLORS.statusGray} label={`${outstanding.length} outstanding`} />
            )}
            {late > 0 && (
              <CountChip bg={COLORS.redBg} text={COLORS.redText} label={`${late} over ${BRYQ_OVERDUE_DAYS} days`} />
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="space-y-3 pb-4">
          {decide.length > 0 && (
            <BryqList heading={`Result in · decide the next step (${decide.length})`}>
              {decide.map((r) => (
                <BryqRow
                  key={`${r.opportunityId}-${r.test.key}`}
                  row={r}
                  dot={COLORS.statusGreen}
                  status={`${r.test.label}${r.test.score !== null ? ` · ${r.test.score}%` : ''} · done ${fmtDate(r.test.completedAt)}`}
                />
              ))}
            </BryqList>
          )}
          {outstanding.length > 0 && (
            <BryqList heading={`Bryq outstanding (${outstanding.length})`}>
              {outstanding.map((r) => {
                const days = r.daysWaiting ?? 0;
                const dot = days >= BRYQ_OVERDUE_DAYS ? COLORS.statusRed : days >= BRYQ_LATE_DAYS ? COLORS.statusOrange : COLORS.statusGray;
                const mode = r.test.mode === 'parallel' ? ' · with interview' : r.test.mode === 'filter' ? ' · filter step' : '';
                return (
                  <BryqRow
                    key={`${r.opportunityId}-${r.test.key}`}
                    row={r}
                    dot={dot}
                    status={`${r.test.label}${mode} · sent ${days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}`}
                  />
                );
              })}
            </BryqList>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function CountChip({ bg, text, label }: { bg: string; text: string; label: string }) {
  return (
    <span className="rounded-full px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: bg, color: text }}>
      {label}
    </span>
  );
}

function BryqList({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold tracking-wide text-gray-400">{heading}</div>
      <div className="space-y-2 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
        {children}
      </div>
    </div>
  );
}

function BryqRow({ row, dot, status }: { row: BryqBoardRow; dot: string; status: string }) {
  return (
    <SlaCard
      dot={<Dot color={dot} />}
      name={row.name}
      leverUrl={row.leverUrl || '#'}
      stageName={`${row.roleTitle}${row.stageName ? ` · ${row.stageName}` : ''}`}
      stuck={<span className="truncate text-xs text-gray-600">{status}</span>}
      actions={<BryqChip candidateName={row.name} opportunityId={row.opportunityId} />}
    />
  );
}
